import { useEffect, useRef, useMemo } from 'react';
import { CombinedError } from 'urql';

import { isStaleBuildError, StaleBuildError } from '../graphql/stale-build-error';

const extractErrorDetails = (error: CombinedError | undefined) => {
  const extensions = error?.graphQLErrors[0]?.extensions;
  const messageKeyExtension = extensions?.['messageKey'];

  return {
    isUserFacing: extensions?.['isUserFacing'] === true,
    messageKey: typeof messageKeyExtension === 'string' ? messageKeyExtension : undefined,
  };
};

const useHandleUserFacingError = <T extends string>(
  error: CombinedError | undefined,
  handledErrors: T[] = []
): T | undefined => {
  const errorThrown = useRef(false);

  const { isUserFacing, messageKey } = useMemo(() => extractErrorDetails(error), [error]);

  const isHandled = isUserFacing && messageKey !== undefined && handledErrors.includes(messageKey as T);

  useEffect(() => {
    if (error && !isHandled && !errorThrown.current) {
      errorThrown.current = true;
      if (isStaleBuildError(error)) throw new StaleBuildError(error.message);
      throw error;
    }
  }, [error, isHandled]);

  return useMemo<T | undefined>(
    () => (isHandled && messageKey ? (messageKey as T) : undefined),
    [isHandled, messageKey]
  );
};

export { useHandleUserFacingError };
